import React, { useEffect, useState } from 'react';

import {
  getChecklistItems,
  getChecklistSession,
  getChecklistSessionsByCar,
} from '../../../api/checklistsApi';
import { ehFamiliaSaida } from '../../../shared/checklist-familias';
import '../../../shared/feedback/erro-de-tela.css';
import './ComparativoSaidaOriginal.css';

interface ComparativoSaidaOriginalProps {
  /** Id do atendimento de ORIGEM (vem de `car.posEvento.origemCarId`). */
  origemCarId: string;
  /** Data de entrega do serviço original, só para dar contexto ao título. */
  origemEntregueEm?: string | null;
}

type Estado = 'carregando' | 'ok' | 'sem-saida' | 'erro';

/**
 * Como o veículo SAIU do serviço original (at-96).
 *
 * É a peça mais valiosa e mais barata da entrega: "avaliar o nosso serviço" é comparar como o
 * carro saiu com como ele voltou — e quem preenche a vistoria de entrada do retorno precisa
 * disso ao lado, não em outra aba.
 *
 * Sem rota nova: as sessões do atendimento de origem já são legíveis.
 *
 * 🔴 TRÊS estados que não podem se parecer (regra da at-60):
 *   - o original **não tem** checklist de saída — legítimo, porque a esteira ENTREGA também é
 *     alcançável pelo `updateStatus` manual, sem checklist;
 *   - a consulta **falhou** — a tela diz que não conseguiu verificar;
 *   - carregou.
 * Colapsar os dois primeiros faria a tela afirmar "o serviço saiu sem vistoria" sobre um
 * checklist que existe.
 *
 * ⚠️ É LEITURA: nada de controles de edição, seleção de mídia ou exclusão.
 */
const ComparativoSaidaOriginal: React.FC<ComparativoSaidaOriginalProps> = ({
  origemCarId,
  origemEntregueEm,
}) => {
  const [estado, setEstado] = useState<Estado>('carregando');
  const [sessao, setSessao] = useState<any>(null);
  const [itens, setItens] = useState<{ templates: any[]; answers: any[] } | null>(null);
  const [aberto, setAberto] = useState(false);

  useEffect(() => {
    let ativo = true;

    (async () => {
      setEstado('carregando');
      try {
        const sessoes = await getChecklistSessionsByCar(origemCarId);
        // Por FAMÍLIA: o original pode ele mesmo ter sido um pós-evento (cadeia é permitida).
        const saida = (sessoes || []).find((s: any) => ehFamiliaSaida(String(s?.type || '').toUpperCase()));

        if (!ativo) return;
        if (!saida?.id) {
          setEstado('sem-saida');
          return;
        }

        const [detalhe, respostas] = await Promise.all([
          getChecklistSession(saida.id),
          getChecklistItems(saida.id).catch(() => null),
        ]);

        if (!ativo) return;
        setSessao(detalhe);
        setItens(respostas);
        setEstado('ok');
      } catch {
        if (ativo) setEstado('erro');
      }
    })();

    return () => {
      ativo = false;
    };
  }, [origemCarId]);

  const dataEntrega = origemEntregueEm
    ? new Date(origemEntregueEm).toLocaleDateString('pt-BR')
    : null;

  return (
    <div className="comparativo-saida">
      <button
        type="button"
        className="comparativo-saida-cabecalho"
        onClick={() => setAberto((v) => !v)}
        aria-expanded={aberto}
      >
        <span className="comparativo-saida-titulo">
          Como o veículo saiu do serviço anterior
          {dataEntrega ? ` (entregue em ${dataEntrega})` : ''}
        </span>
        <span className="comparativo-saida-toggle">{aberto ? 'ocultar' : 'ver'}</span>
      </button>

      {aberto && (
        <div className="comparativo-saida-corpo">
          {estado === 'carregando' && (
            <p className="comparativo-saida-texto">Consultando o checklist de saída do serviço anterior...</p>
          )}

          {estado === 'erro' && (
            <div className="erro-de-tela">
              Não foi possível consultar o checklist de saída do serviço anterior. Isto não
              significa que ele não exista — recarregue a página; se persistir, acione a TI.
            </div>
          )}

          {estado === 'sem-saida' && (
            <p className="comparativo-saida-texto">
              O serviço anterior não tem checklist de saída registrado. A entrega pode ter sido
              feita sem a vistoria de saída.
            </p>
          )}

          {estado === 'ok' && (
            <>
              {sessao?.completedAt && (
                <p className="comparativo-saida-meta">
                  Vistoria de saída concluída em {new Date(sessao.completedAt).toLocaleString('pt-BR')}
                </p>
              )}

              {itens && itens.answers?.length > 0 ? (
                <ul className="comparativo-saida-itens">
                  {itens.answers.map((a: any) => {
                    const template = itens.templates?.find((t: any) => t.key === a.templateKey);
                    return (
                      <li key={a.templateKey} className="comparativo-saida-item">
                        <span className="comparativo-saida-item-label">{template?.label || a.templateKey}</span>
                        <span className="comparativo-saida-item-status">{a.status}</span>
                        {a.note && <span className="comparativo-saida-item-nota">{a.note}</span>}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="comparativo-saida-texto">
                  A saída do serviço anterior não tem itens respondidos — o checklist de saída
                  registra apenas as imagens.
                </p>
              )}

              {sessao?.observation && (
                <p className="comparativo-saida-texto">
                  <strong>Observações de saída:</strong> {sessao.observation}
                </p>
              )}

              {Array.isArray(sessao?.media) && sessao.media.length > 0 && (
                <div className="comparativo-saida-midias">
                  <p className="comparativo-saida-meta">
                    Imagens da saída anterior ({sessao.media.length}) — clique para abrir:
                  </p>
                  <div className="comparativo-saida-tiras">
                    {sessao.media.map((m: any) => (
                      <a
                        key={m.id}
                        href={m.url}
                        target="_blank"
                        rel="noreferrer"
                        className="comparativo-saida-thumb"
                        title="Abrir em nova aba"
                      >
                        {m.type === 'VIDEO' ? (
                          <span className="comparativo-saida-video">vídeo</span>
                        ) : (
                          <img src={m.url} alt="Imagem da saída anterior" loading="lazy" />
                        )}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ComparativoSaidaOriginal;
